import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";
import ProductDetail from "../../../components/app/Products/ProductDetail";
import AllProductContext from "../../../context/allProducts/AllProductsContext";
import NoContent from "../../errors/NoContent";
import ProductsContainer from "../../../components/app/Products/ProductsContainer";
import AllCategoriesContext from "../../../context/allCategories/AllCategoriesContext";
import { Breadcrumbs, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import AllReviewsContext from "../../../context/allReviews/AllReviewsContext";
import ReviewsAPI from "../../../api/firebase/ReviewsAPI";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import {
  categoryHasProducts,
  filterProductsForCategory,
  getCategoryImageUrl,
  getVisibleSubcategories,
  groupProductsBySubcategories,
  productInCategory,
} from "../../../utils/categoryProducts";
import "./styles.css";
import "../home/styles.css";

const CategorySidebarItemIcon = ({ category }) => {
  const imageUrl = getCategoryImageUrl(category);
  const [failedUrl, setFailedUrl] = useState(null);

  const hasFailed = failedUrl === imageUrl;

  if (imageUrl && !hasFailed) {
    return (
      <img
        src={imageUrl}
        alt={category?.name || "Category"}
        className="collection-sidebar-item-thumb"
        onError={() => setFailedUrl(imageUrl)}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className="collection-sidebar-item-icon-fallback"
      aria-hidden="true"
      data-testid="category-icon-fallback"
    >
      {category?.name?.charAt(0).toUpperCase() || ""}
    </div>
  );
};

const CollectionsPage = () => {
  const loaderData = useRouteLoaderData("storefront");
  const allProductsContext = useContext(AllProductContext);
  const productsArray = useMemo(() => {
    if (Array.isArray(loaderData?.data) && loaderData.data.length > 0) {
      return loaderData.data;
    }
    if (Array.isArray(allProductsContext?.state) && allProductsContext.state.length > 0) {
      return allProductsContext.state;
    }
    return [];
  }, [loaderData, allProductsContext?.state]);

  const allCategoriesContext = useContext(AllCategoriesContext);
  const allReviewsContext = useContext(AllReviewsContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const mobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [allCategoriesArr, setAllCategoriesArr] = useState(
    Array.isArray(allCategoriesContext?.state) ? allCategoriesContext.state : []
  );
  const [isLoading, setIsLoading] = useState(true);
  const { categoryId, itemId } = useParams();
  const [filteredProductsArr, setFilteredProductsArr] = useState([]);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [categorySearch, setCategorySearch] = useState("");

  const basePath = "/collections";
  const tabsScrollRef = useRef(null);

  // 1. Fetch reviews for accurate product star ratings
  useEffect(() => {
    const reviewsAPI = new ReviewsAPI();
    reviewsAPI
      .getReviews()
      .then((res) => {
        if (res && res.success && allReviewsContext?.setState) {
          allReviewsContext.setState(res.data);
        }
      })
      .catch((err) => {
        console.log("Error loading reviews:", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Fetch categories if not already populated in context
  useEffect(() => {
    if (!allCategoriesContext?.state || allCategoriesContext.state.length === 0) {
      const productAPI = new ProductsAPI();
      productAPI
        .getCategories()
        .then((res) => {
          if (res && res.success && Array.isArray(res.data)) {
            if (allCategoriesContext?.setState) {
              allCategoriesContext.setState(res.data);
            }
            setAllCategoriesArr(res.data);
          }
        })
        .catch((err) => {
          console.log("Error loading categories:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Update category state when context changes
  useEffect(() => {
    if (allCategoriesContext?.state && allCategoriesContext.state.length > 0) {
      setAllCategoriesArr(allCategoriesContext.state);
      setIsLoading(false);
    }
  }, [allCategoriesContext?.state]);

  // 4. Update loading state when categories are available
  useEffect(() => {
    if (allCategoriesArr.length > 0) {
      setIsLoading(false);
    }
  }, [allCategoriesArr]);

  // 5. Update filtered products when category or products change
  useEffect(() => {
    if (allProductsContext?.setState && productsArray.length > 0) {
      allProductsContext.setState(productsArray);
    }
    if (categoryId !== undefined) {
      const filteredArray = filterProductsForCategory(
        productsArray,
        categoryId,
        allCategoriesArr
      );
      setFilteredProductsArr(filteredArray);
    } else {
      setFilteredProductsArr(productsArray);
    }
  }, [categoryId, allCategoriesArr, productsArray, allProductsContext]);

  // 6. Reset selected subcategory when changing category
  useEffect(() => {
    setSelectedSubId(null);
  }, [categoryId]);

  // 7. Auto-scroll the active category pill into view on mobile
  useEffect(() => {
    if (!tabsScrollRef.current) return;
    const activeBtn = tabsScrollRef.current.querySelector(".collection-pill-btn.is-active");
    if (activeBtn?.scrollIntoView) {
      activeBtn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [categoryId]);

  // 7. Compute available top-level categories for the sidebar (hide if no products)
  const availableCategories = useMemo(() => {
    if (!Array.isArray(allCategoriesArr) || allCategoriesArr.length === 0) {
      return [];
    }
    const prods = Array.isArray(productsArray) ? productsArray : [];
    const parents = allCategoriesArr.filter((item) => {
      const isParent =
        !item.parentCategory ||
        !item.parentCategory.id ||
        item.parentCategory.id === "None" ||
        item.parentCategory.name === "None";
      if (!isParent) return false;
      return categoryHasProducts(item.id, prods, allCategoriesArr);
    });

    return parents;
  }, [allCategoriesArr, productsArray]);

  // Filter categories by sidebar search
  const displayedCategories = useMemo(() => {
    if (!categorySearch.trim()) return availableCategories;
    const q = categorySearch.trim().toLowerCase();
    return availableCategories.filter((cat) =>
      cat.name?.toLowerCase().includes(q)
    );
  }, [availableCategories, categorySearch]);

  // Handle Product Detail View
  if (itemId !== undefined) {
    const productDetail = productsArray.find((e) => e.id === itemId);
    if (productDetail !== undefined) {
      return !isLoading && <ProductDetail product={productDetail} />;
    } else {
      return !isLoading && <NoContent />;
    }
  }

  // Left Sidebar: Categories Navigation
  const categorySidebar = (
    <div className="collection-sidebar-panel">
      <div className="collection-sidebar-header">
        <div className="collection-sidebar-title-row">
          <div className="collection-sidebar-icon-badge" aria-hidden="true">
            <GridViewRoundedIcon sx={{ fontSize: 18 }} />
          </div>
          <h2 className="collection-sidebar-title">Categories</h2>
          <span className="collection-sidebar-total-badge">
            {availableCategories.length}
          </span>
        </div>
        <p className="collection-sidebar-subtitle">Explore our collections</p>

        <div className="collection-sidebar-search">
          <SearchRoundedIcon className="collection-sidebar-search-icon" />
          <input
            type="text"
            placeholder="Search categories..."
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            className="collection-sidebar-search-input"
            aria-label="Search categories"
          />
          {categorySearch && (
            <button
              type="button"
              className="collection-sidebar-search-clear"
              onClick={() => setCategorySearch("")}
              aria-label="Clear category search"
            >
              <ClearRoundedIcon sx={{ fontSize: 14 }} />
            </button>
          )}
        </div>
      </div>

      <div
        className="collection-sidebar-list"
        role="tablist"
        aria-label="Category selection"
      >
        {/* All Collections button (shown when not searching) */}
        {!categorySearch.trim() && (
          <button
            type="button"
            className={`collection-sidebar-item ${!categoryId ? "is-active" : ""}`}
            onClick={() => {
              setSelectedSubId(null);
              navigate(basePath);
            }}
          >
            <div className="collection-sidebar-item-icon-fallback" aria-hidden="true">
              <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />
            </div>
            <span className="collection-sidebar-item-label">All Collections</span>
            <span className="collection-sidebar-item-count">
              {productsArray?.length || 0}
            </span>
          </button>
        )}

        {/* All Available Categories */}
        {displayedCategories.map((cat) => {
          const isCatActive = categoryId === cat.id;
          const catProducts = filterProductsForCategory(
            productsArray,
            cat.id,
            allCategoriesArr
          );
          const catSubcategories = getVisibleSubcategories(
            cat.id,
            allCategoriesArr,
            productsArray
          );
          const hasSubcategories = catSubcategories.length > 0;

          return (
            <React.Fragment key={cat.id}>
              <button
                type="button"
                className={`collection-sidebar-item ${isCatActive ? "is-active" : ""}`}
                onClick={() => {
                  setSelectedSubId(null);
                  navigate(`${basePath}/${cat.id}`);
                }}
              >
                <CategorySidebarItemIcon category={cat} />
                <span className="collection-sidebar-item-label">{cat.name}</span>
                <span className="collection-sidebar-item-count">
                  {catProducts.length}
                </span>
                {hasSubcategories && (
                  <span className="collection-sidebar-item-chevron" aria-hidden="true">
                    {isCatActive ? (
                      <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <KeyboardArrowRightRoundedIcon sx={{ fontSize: 18 }} />
                    )}
                  </span>
                )}
              </button>

              {/* Subcategories (when category is selected and has subcategories) */}
              {isCatActive && hasSubcategories && (
                <div
                  className="collection-sidebar-sublist"
                  role="tablist"
                  aria-label={`${cat.name} subcategories`}
                >
                  <button
                    type="button"
                    className={`collection-sidebar-subitem ${
                      !selectedSubId ? "is-active" : ""
                    }`}
                    onClick={() => setSelectedSubId(null)}
                  >
                    <div className="collection-sidebar-subitem-title-wrap">
                      <span className="collection-sidebar-subitem-dot" aria-hidden="true" />
                      <span>All {cat.name}</span>
                    </div>
                    <span className="collection-sidebar-subitem-count">
                      {catProducts.length}
                    </span>
                  </button>
                  {catSubcategories.map((sub) => {
                    const subCount = productsArray.filter((p) =>
                      productInCategory(p, sub.id)
                    ).length;
                    return (
                      <button
                        key={sub.id}
                        type="button"
                        className={`collection-sidebar-subitem ${
                          selectedSubId === sub.id ? "is-active" : ""
                        }`}
                        onClick={() => setSelectedSubId(sub.id)}
                      >
                        <div className="collection-sidebar-subitem-title-wrap">
                          <span className="collection-sidebar-subitem-dot" aria-hidden="true" />
                          <span>{sub.name}</span>
                        </div>
                        <span className="collection-sidebar-subitem-count">
                          {subCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {displayedCategories.length === 0 && (
          <div className="collection-sidebar-empty">
            <p>No categories match "{categorySearch}"</p>
            <button type="button" onClick={() => setCategorySearch("")}>
              Clear search
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Horizontal Category Tabs for Mobile
  const categoryTabsMobile = (
    <div
      ref={tabsScrollRef}
      className="collection-tabs-scroll"
      role="tablist"
      aria-label="All Categories"
    >
      <button
        type="button"
        className={`collection-pill-btn ${!categoryId ? "is-active" : ""}`}
        onClick={() => {
          setSelectedSubId(null);
          navigate(basePath);
        }}
      >
        <span className="collection-pill-icon-wrap" aria-hidden="true">
          <StorefrontOutlinedIcon sx={{ fontSize: 14 }} />
        </span>
        <span>All</span>
        <span className="collection-pill-count">{productsArray?.length || 0}</span>
      </button>
      {availableCategories.map((cat) => {
        const isCatActive = categoryId === cat.id;
        const count = filterProductsForCategory(
          productsArray,
          cat.id,
          allCategoriesArr
        ).length;
        const thumbUrl = getCategoryImageUrl(cat);
        return (
          <button
            key={cat.id}
            type="button"
            className={`collection-pill-btn ${isCatActive ? "is-active" : ""}`}
            onClick={() => {
              setSelectedSubId(null);
              navigate(`${basePath}/${cat.id}`);
            }}
          >
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt=""
                aria-hidden="true"
                className="collection-pill-thumb"
                loading="lazy"
              />
            ) : (
              <span className="collection-pill-icon-wrap collection-pill-letter" aria-hidden="true">
                {cat.name?.charAt(0).toUpperCase() || ""}
              </span>
            )}
            <span>{cat.name}</span>
            <span className="collection-pill-count">{count}</span>
          </button>
        );
      })}
    </div>
  );

  // Category Selected View (e.g. /collections/:categoryId)
  if (categoryId !== undefined) {
    let parentCategories = [];
    const categoryDetail = allCategoriesArr.filter((item) => item.id === categoryId);

    const getParentCategory = (_category) => {
      if (_category.parentCategory && _category.parentCategory.id !== "None") {
        const pCat = allCategoriesArr.filter(
          (item) => item.id === _category.parentCategory.id
        );
        if (pCat.length > 0) {
          parentCategories.push(pCat[0]);
          getParentCategory(pCat[0]);
        }
      }
    };

    let breadcrumbs = null;
    if (categoryDetail.length > 0) {
      getParentCategory(categoryDetail[0]);
      parentCategories.reverse();
      breadcrumbs = [
        <Link underline="hover" key="1" color="inherit" to="/">
          Home
        </Link>,
        <Link underline="hover" key="2" color="inherit" to={basePath}>
          Collections
        </Link>,
      ];
      parentCategories.forEach((cat, index) => {
        const pathTo = `${basePath}/${cat.id}`;
        breadcrumbs.push(
          <Link underline="hover" key={index + 3} color="inherit" to={pathTo}>
            {cat.name}
          </Link>
        );
      });
      breadcrumbs.push(
        <Typography key={parentCategories.length + 3} color="text.primary">
          {categoryDetail[0].name}
        </Typography>
      );
    }

    const subCategories = getVisibleSubcategories(
      categoryId,
      allCategoriesArr,
      productsArray
    );
    const categoryName = categoryDetail[0]?.name || categoryId.replace(/_/g, " ");

    const productSections =
      subCategories.length > 0
        ? (() => {
            const { groups, ungrouped } = groupProductsBySubcategories(
              filteredProductsArr,
              subCategories
            );
            const sections = [...groups];
            if (ungrouped.length > 0) {
              sections.push({
                id: `${categoryId}-other`,
                name: categoryName,
                products: ungrouped,
              });
            }
            return sections.length
              ? sections
              : [{ id: categoryId, name: categoryName, products: [] }];
          })()
        : [{ id: categoryId, name: categoryName, products: filteredProductsArr }];

    const grouped = subCategories.length > 0;
    const visibleSections = selectedSubId
      ? productSections.filter((section) => section.id === selectedSubId)
      : productSections;
    const visibleProductCount = visibleSections.reduce(
      (total, section) => total + section.products.length,
      0
    );

    const productList = (showEmpty) => {
      if (showEmpty) {
        return (
          <ProductsContainer
            heading=""
            productsArray={[]}
            showCarousel={false}
            emptyMessage="No products found in this category yet. Check back soon or explore our other collections."
          />
        );
      }
      return visibleSections.map((section) => (
        <section
          key={section.id}
          id={`collection-${section.id}`}
          className="collection-section"
        >
          <ProductsContainer
            heading={grouped && !selectedSubId ? section.name : ""}
            headingAlign="left"
            productCount={grouped && !selectedSubId ? section.products.length : null}
            productsArray={section.products}
            showCarousel={false}
            addToCartEnable={true}
            catalogGrid={true}
          />
        </section>
      ));
    };

    const breadcrumbBar =
      breadcrumbs !== null ? (
        <div className="collection-breadcrumb">
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            {breadcrumbs}
          </Breadcrumbs>
        </div>
      ) : null;

    const totalProducts = visibleProductCount;
    const noMatches = !isLoading && totalProducts === 0;
    const parentCat = parentCategories[parentCategories.length - 1];
    const backTo = parentCat ? `${basePath}/${parentCat.id}` : basePath;
    const backLabel = parentCat ? parentCat.name : "Collections";

    const subcategoryJumps = grouped ? (
      <div className="collection-jumps" role="tablist" aria-label="Subcategories">
        <button
          type="button"
          className={!selectedSubId ? "is-active" : ""}
          onClick={() => setSelectedSubId(null)}
        >
          All
          <span>{filteredProductsArr.length}</span>
        </button>
        {subCategories.map((sub) => {
          const count =
            productSections.find((section) => section.id === sub.id)?.products.length || 0;
          return (
            <button
              key={sub.id}
              type="button"
              className={selectedSubId === sub.id ? "is-active" : ""}
              onClick={() => setSelectedSubId(sub.id)}
            >
              {sub.name}
              <span>{count}</span>
            </button>
          );
        })}
      </div>
    ) : null;

    const collectionMain = (
      <div className="collection-main">
        <div className="collection-hero">
          <div className="collection-hero-row">
            <h1>{categoryName}</h1>
            <p className="collection-count">
              {totalProducts} {totalProducts === 1 ? "product" : "products"}
            </p>
          </div>
          {subcategoryJumps}
        </div>
        <div className="collection-list">{productList(noMatches)}</div>
      </div>
    );

    // Desktop Category Page
    if (!isLoading && !mobileScreen) {
      return (
        <div className="collection-page">
          {breadcrumbBar}
          <div className="collection-layout">
            <aside className="collection-sidebar">{categorySidebar}</aside>
            {collectionMain}
          </div>
        </div>
      );
    }

    // Mobile Category Page
    return (
      <div className="collection-page collection-page-mobile">
        <div className="collection-mobile-chrome">
          <div className="collection-mobile-header-row">
            <Link to={backTo} className="collection-mobile-back-btn" aria-label={`Back to ${backLabel}`}>
              <ArrowBackRoundedIcon sx={{ fontSize: 24 }} />
            </Link>
            <h1 className="collection-mobile-title">
              {categoryName}
              <span className="collection-count-inline">
                ({totalProducts})
              </span>
            </h1>
          </div>
          {categoryTabsMobile}
          {subcategoryJumps}
        </div>
        <div className="collection-list">{productList(noMatches)}</div>
      </div>
    );
  }

  // All Collections Landing View (e.g. /collections)
  const breadcrumbBarLanding = (
    <div className="collection-breadcrumb">
      <Breadcrumbs separator="›" aria-label="breadcrumb">
        <Link to="/" style={{ color: "#5b6b68", textDecoration: "none" }}>
          Home
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 600 }}>
          Collections
        </Typography>
      </Breadcrumbs>
    </div>
  );

  // Desktop All Collections
  if (!isLoading && !mobileScreen) {
    return (
      <div className="collection-page">
        {breadcrumbBarLanding}
        <div className="collection-layout">
          <aside className="collection-sidebar">{categorySidebar}</aside>
          <div className="collection-main">
            <div className="collection-hero">
              <div className="collection-hero-row">
                <h1>All Collections</h1>
                <p className="collection-count">
                  {productsArray.length} {productsArray.length === 1 ? "product" : "products"}
                </p>
              </div>
              <p className="collection-landing-copy" style={{ margin: "0.5rem 0 0" }}>
                Ayurvedic medicines, herbal supplements, oils, and daily wellness essentials.
              </p>
            </div>
            <div className="collection-list" style={{ marginTop: "1rem" }}>
              <ProductsContainer
                heading="All Products"
                headingAlign="left"
                productCount={productsArray.length}
                productsArray={productsArray}
                showCarousel={false}
                addToCartEnable={true}
                catalogGrid={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile All Collections
  return (
    <div className="collection-page collection-page-mobile">
      <div className="collection-mobile-chrome">
        <div className="collection-mobile-header-row">
          <Link to="/" className="collection-mobile-back-btn" aria-label="Back to Home">
            <ArrowBackRoundedIcon sx={{ fontSize: 24 }} />
          </Link>
          <h1 className="collection-mobile-title">
            All Collections
            <span className="collection-count-inline">
              ({productsArray.length})
            </span>
          </h1>
        </div>
        {categoryTabsMobile}
      </div>
      <div className="collection-list" style={{ padding: "1rem" }}>
        <ProductsContainer
          heading="All Products"
          headingAlign="left"
          productCount={productsArray.length}
          productsArray={productsArray}
          showCarousel={false}
          addToCartEnable={true}
          catalogGrid={true}
        />
      </div>
    </div>
  );
};

export default CollectionsPage;
